import { CommandList } from "@/components/shadcn/command";
import type { DrugSuggestionGroup } from "@/hooks/use-drug-suggestions";
import { buildFlatRows, isFuzzyOnly } from "@/components/search/suggestion-rows";
import { ReportStatus, SuggestionRow } from "@/components/search/suggestion-row";
import {
  DropdownError,
  DropdownLoading,
} from "@/components/search/dropdown-states";

// A plain button, not a cmdk item, so it is never cmdk's active item: with no
// rows a doctor's Enter must read as "search what I typed" (the search box
// submits the text itself), and under a fuzzy list Enter commits the active
// row. Mouse and tap only.
function FreeTextFallback({
  query,
  onClick,
  className = "",
}: {
  query: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-11 w-full items-start border-l-2 border-l-transparent px-3 py-2 text-left transition-colors hover:border-l-brand hover:bg-accent focus-visible:border-l-brand focus-visible:bg-accent focus-visible:outline-none ${className}`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-start justify-between gap-3">
          <span className="text-sm font-semibold text-foreground">
            Szukaj „{query}” mimo to
          </span>
          <ReportStatus status="new" />
        </span>
        <span className="text-sm text-muted-foreground">
          Nie znaleźliśmy „{query}” w rejestrze. Spróbujemy rozpoznać nazwę i
          utworzyć nowy raport.
        </span>
      </span>
    </button>
  );
}

export function DrugSuggestionDropdown({
  groups,
  query,
  loading,
  error,
  stale,
  onCommit,
  onRetry,
  onFreeTextSearch,
}: {
  groups: DrugSuggestionGroup[];
  query: string;
  loading: boolean;
  error: boolean;
  stale: boolean;
  onCommit: (submit: string, label: string) => void;
  onRetry: () => void;
  onFreeTextSearch: () => void;
}) {
  if (error) {
    return <DropdownError onRetry={onRetry} />;
  }

  if (loading && groups.length === 0) {
    return <DropdownLoading />;
  }

  const rows = buildFlatRows(groups);
  const shownQuery = query.trim();

  if (rows.length === 0) {
    return <FreeTextFallback query={shownQuery} onClick={onFreeTextSearch} />;
  }

  const fuzzyOnly = isFuzzyOnly(rows);

  return (
    <>
      <CommandList label="Podpowiedzi" className="max-h-[min(20rem,45dvh)]">
        {fuzzyOnly && (
          // Hidden from AT like cmdk's own group headings: a listbox may hold
          // only options, and the search box's live region says the same.
          <div
            aria-hidden
            className="px-3 pb-1 pt-2 text-sm text-muted-foreground"
          >
            Nie znaleźliśmy „{shownQuery}”. Podobne nazwy:
          </div>
        )}
        {rows.map((row, i) => (
          <SuggestionRow
            key={row.key}
            row={row}
            value={`r${i}`}
            query={query}
            stale={stale}
            onCommit={onCommit}
          />
        ))}
      </CommandList>
      {fuzzyOnly && (
        <FreeTextFallback
          query={shownQuery}
          onClick={onFreeTextSearch}
          className="border-t border-border"
        />
      )}
    </>
  );
}
